import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import { Alert, Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { CENTS_TO_DOLLARS_CONVERSION_FACTOR } from '../../../SubscriptionManager.constants';
import {
  ePlanCurrency,
  eProductType,
} from '../../../SubscriptionManager.enums';
import CardSelector from '../../CardSelector/CardSelector.component';

export default function ManageAdvancedLeadbotModal() {
  const {
    selectedPaymentMethod,
    toggleAddPaymentMethodModal,
    showManageAdvancedLeadbotModal,
    toggleManageAdvancedLeadbotModal,
    updateAdvancedLeadbotPlan,
    updatingPlan,
    products,
  } = useSubscriptionManagerContext();
  const { subscription } = useSubscriptionContext();
  const { leadbotLevelsAccess: advancedLeadbotEnabled } = useACLContext();
  const { t, i18n } = useTranslation('settings/SubscriptionManager');

  const longDateConfig: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const conversionFormattedDate = subscription.endDate.toLocaleString(
    i18n.language,
    longDateConfig,
  );

  // Using a fixed cost for Advanced Leadbot based on existing code
  const advancedLeadbotCost =
    products?.allRelatedProducts?.find(
      (product) => product.productType === eProductType.LEADBOT_LEVELS,
    )?.price ||
    (subscription.currency === ePlanCurrency.MXN && 5000) ||
    5000;

  return (
    <Modal
      open={showManageAdvancedLeadbotModal}
      backdrop
      onClose={() => {
        toggleManageAdvancedLeadbotModal(false);
      }}
      className="rounded-lg p-0 min-w-[38.5rem]"
      data-testid="subscription-modal"
    >
      <Modal.Header className="flex justify-between items-center mb-0 p-4 border-b">
        <h1 className="text-lg w-full">
          {t('addOns.manageAdvancedLeadbot.title')}
        </h1>
        <XMarkIcon
          onClick={() => {
            toggleManageAdvancedLeadbotModal(false);
          }}
          className="size-6"
        />
      </Modal.Header>
      <Modal.Body className="py-6 px-4 form-control gap-4">
        {!advancedLeadbotEnabled ? (
          <div className="flex flex-col gap-1">
            <p>{t('addOns.manageAdvancedLeadbot.paymentMethod')}</p>
            {isEmpty(selectedPaymentMethod.paymentMethodId) ? (
              <Button
                className="SubscriptionCheckout__paymentMethods__addPaymentMethodButton"
                onClick={() => toggleAddPaymentMethodModal(true)}
                color="ghost"
                fullWidth
                data-testid="add-payment-method-button"
              >
                {t('page.SubscriptionCheckout.addPaymentMethodButton')}
              </Button>
            ) : (
              <>
                <CardSelector />
                <Button
                  onClick={() => toggleAddPaymentMethodModal(true)}
                  variant="link"
                  size="sm"
                  className="text-neutral-content w-fit px-0"
                >
                  {t('page.SubscriptionCheckout.addPaymentMethod')}
                </Button>
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {!advancedLeadbotEnabled ? (
            <p className="text-sm text-neutral-500">
              {t('addOns.manageAdvancedLeadbot.proportionalCharge')}
              <br />
              {t('addOns.manageAdvancedLeadbot.proportionalChargeDescription', {
                date: conversionFormattedDate,
                cost: advancedLeadbotCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          ) : (
            <>
              <p>{t('addOns.manageAdvancedLeadbot.deactivationWarning')}</p>
              <Alert
                icon={<InformationCircleIcon className="size-6 text-primary" />}
                className="border-primary p-2"
              >
                <span className="text-sm">
                  <Trans
                    i18nKey="settings/SubscriptionManager:addOns.manageAdvancedLeadbot.deactivationAlert"
                    values={{ date: conversionFormattedDate }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </span>
              </Alert>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions className="p-4 bg-base-300 items-center mt-0" data-testid="modal-actions">
        {!advancedLeadbotEnabled ? (
          <>
            <p className="font-bold text-xl">
              {t('addOns.manageAdvancedLeadbot.total', {
                cost: advancedLeadbotCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
              })}
            </p>
            <p className="text-xs font-normal flex-1 ">
              {t('addOns.manageAdvancedLeadbot.monthlyRecurring', {
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          </>
        ) : null}
        <Button
          color={advancedLeadbotEnabled ? 'error' : 'primary'}
          size="sm"
          className="rounded"
          onClick={() => updateAdvancedLeadbotPlan(!advancedLeadbotEnabled)}
          loading={updatingPlan}
          disabled={
            isEmpty(selectedPaymentMethod.paymentMethodId) || updatingPlan
          }
          data-testid="modal-action-button"
        >
          {advancedLeadbotEnabled
            ? t('addOns.manageAdvancedLeadbot.confirmDeactivation')
            : t('addOns.manageAdvancedLeadbot.buyNow')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
